import type { HomeAction, HomeState } from "../types";

/** Walk through the gate sequence and stop at the first unmet gate. */
export function advanceThroughGates(
  state: HomeState,
  hasSub: boolean
): HomeState {
  const { paywallTriggerPoint } = state.config;

  // Gate 1: immediate paywall? (earliest possible trigger)
  if (!hasSub && paywallTriggerPoint === "immediate") {
    return { ...state, step: "paywall" };
  }

  // Gate 2: paywall before select-patient?
  if (!hasSub && paywallTriggerPoint === "before_select_patient") {
    return { ...state, step: "paywall" };
  }

  // All home gates passed
  // If a patient was preselected (e.g. from triage CTA), skip straight to
  // the loading overlay instead of flashing the select-patient modal.
  if (state.config.preselectedPatientId) {
    return { ...state, step: "navigating-to-intake" };
  }

  return { ...state, step: "select-patient" };
}

/** Synchronous reducer — pure state transitions only, no side effects. */
export function homeReducer(prev: HomeState, action: HomeAction): HomeState {
  const hasSub = prev.config.hasSubscription || prev.subscriptionJustCompleted;

  switch (action.type) {
    case "START_VISIT":
      return advanceThroughGates({ ...prev, visitorIntent: action.intent }, hasSub);

    case "PAYWALL_COMPLETE":
      return advanceThroughGates(
        { ...prev, subscriptionJustCompleted: true },
        true
      );

    case "SELECT_PATIENT_CANCEL":
      return { ...prev, step: "visit-list" };

    case "BLOCK_VISIT":
      return {
        ...prev,
        step: "block-visit",
        blockVisitPatient: action.patient,
      };

    case "BLOCK_VISIT_DISMISS":
      return { ...prev, step: "visit-list", blockVisitPatient: null };

    case "SHOW_PAYWALL":
      return { ...prev, step: "paywall", paywallDismissable: true };

    case "DISMISS_PAYWALL":
      return { ...prev, step: "visit-list", paywallDismissable: false };

    case "MANAGE_PAYMENT":
      return { ...prev, step: "checkout" };

    case "BACK_TO_PAYWALL":
      return { ...prev, step: "paywall" };

    case "UNBORN_CHILD_WARNING":
      return {
        ...prev,
        step: "unborn-child-warning",
        warningChildName: action.childName,
      };

    case "WARNING_DISMISS":
      return { ...prev, step: "visit-list", warningChildName: null, visitorIntent: undefined };

    // Brand-specific reducers (e.g. owlet/reducer.ts) handle their own actions
    // and delegate shared ones here — return prev for any unhandled actions.
    default:
      return prev;
  }
}
